/* global APP */

import { showWarningNotification } from '../../notifications';
import { getCurrentConference } from '../conference/functions';
import MeetHourJS, { MHTrackErrors, browser } from '../lib-meet-hour';
import { gumPending, setAudioMuted } from '../media/actions';
import { CAMERA_FACING_MODE, MEDIA_TYPE, VIDEO_TYPE } from '../media/constants';
import { IGUMPendingState } from '../media/types';
import { getVirtualScreenshareParticipantOwnerId, isScreenShareParticipant } from '../participants/functions';
import {
    getUserSelectedCameraDeviceId,
    getUserSelectedMicDeviceId,
    updateSettings
} from '../settings';

import { TRACK_UPDATED } from './actionTypes';
import loadEffects from './loadEffects';
import loadEffectsForPreview from './loadEffectsP';
import logger from './logger';

let _lastExpectedTrackIssueNotice = {
    at: 0,
    key: ''
};

/**
 * Maps expected local device errors to user-facing notification keys.
 *
 * @param {Error} error - Track creation error.
 * @returns {string}
 */
function _getExpectedTrackIssueMessageKey(error) {
    const name = error?.name;
    const details = `${error?.message || ''} ${error?.gum?.message || ''}`.toLowerCase();

    if (name === MHTrackErrors.TIMEOUT) {
        return 'notify.deviceBusy';
    }

    if (name === MHTrackErrors.PERMISSION_DENIED) {
        return 'notify.devicePermissionBlocked';
    }

    if (name === MHTrackErrors.NOT_FOUND) {
        return 'notify.deviceNotFound';
    }

    if (name === MHTrackErrors.DEVICE_BUSY) {
        // lib-meet-hour maps NotReadableError / TrackStartError here after its
        // built-in single-shot retry has already failed - i.e. the OS still
        // cannot hand the device to getUserMedia (another app is genuinely
        // holding it). The user-facing text is the same as the
        // GENERAL + "in use" message branch below.
        return 'notify.deviceBusy';
    }

    if (name === MHTrackErrors.GENERAL && /(busy|in use|already|not readable|could not start|starting video source)/.test(details)) {
        return 'notify.deviceBusy';
    }

    return '';
}

/**
 * Returns true if the track creation failed because the page itself is going away. Chromium rejects in-flight
 * getUserMedia calls with 'Failed due to shutdown' while the renderer tears down (navigation, tab close, a
 * conference being disposed). Nobody is left to act on it, so it must not be reported as a fault.
 *
 * @param {Error} error - Track creation error.
 * @returns {boolean}
 */
function _isTeardownError(error) {
    return `${error?.message || ''} ${error?.gum?.message || ''}`.toLowerCase().includes('due to shutdown');
}

/**
 * Returns true if track creation already reports this failure to the user by itself (see
 * {@link _notifyExpectedTrackIssue}). Callers that add their own message use this to avoid stacking a second
 * notification on top for the same failure.
 *
 * @param {Error} error - Track creation error.
 * @returns {boolean}
 */
export function isExpectedTrackIssue(error) {
    return error?.name === MHTrackErrors.SCREENSHARING_USER_CANCELED
        || Boolean(_getExpectedTrackIssueMessageKey(error))
        || _isTeardownError(error);
}

/**
 * Displays throttled user notifications for expected device issues.
 *
 * @param {Error} error - Track creation error.
 * @returns {boolean}
 */
function _notifyExpectedTrackIssue(error) {
    if (error?.name === MHTrackErrors.SCREENSHARING_USER_CANCELED) {
        return true;
    }

    const descriptionKey = _getExpectedTrackIssueMessageKey(error);

    if (!descriptionKey || typeof APP === 'undefined' || !APP?.store?.dispatch) {
        return false;
    }

    const key = `${error?.name || 'unknown'}:${descriptionKey}`;
    const now = Date.now();

    if (_lastExpectedTrackIssueNotice.key === key && now - _lastExpectedTrackIssueNotice.at < 10000) {
        return true;
    }

    _lastExpectedTrackIssueNotice = {
        at: now,
        key
    };

    APP.store.dispatch(showWarningNotification({
        titleKey: 'notify.deviceAccessIssueTitle',
        descriptionKey
    }));

    return true;
}


/**
 * Returns root tracks state.
 *
 * @param {Object} state - Global state.
 * @returns {Object} Tracks state.
 */
export const getTrackState = state => state['features/base/tracks'];

/**
   * Higher-order function that returns a selector for a specific participant
   * and media type.
   *
   * @param {Object} participant - Participant reference.
   * @param {MEDIA_TYPE} mediaType - Media type.
   * @returns {Function} Selector.
   */
export const getIsParticipantMediaMuted = (participant, mediaType) =>

/**
       * Bound selector.
       *
       * @param {Object} state - Global state.
       * @returns {boolean} Is the media type muted for the participant.
       */
    state => {
        if (!participant) {
            return;
        }

        const confState = state['features/base/conference'];

        if (participant?.role !== 'moderator') {
            if (mediaType === MEDIA_TYPE.AUDIO && (confState?.lockAudio?.[participant.id] === true || confState?.isAudioMuteAll)) {
                return true;
            }
            if (mediaType === MEDIA_TYPE.VIDEO && (confState?.lockVideo?.[participant.id] === true || confState?.isVideoMuteAll)) {
                return true;
            }
        }

        const tracks = getTrackState(state);

        if (participant?.local) {
            return isLocalTrackMuted(tracks, mediaType);
        } else if (!participant?.isFakeParticipant) {
            return isRemoteTrackMuted(tracks, mediaType, participant.id);
        }

        return true;
    };


/**
 * Checks if the participant is audio muted.
 *
 * @param {Object} participant - Participant reference.
 * @param {Object} state - Global state.
 * @returns {boolean} - Is audio muted for the participant.
 */
export function isParticipantAudioMuted(participant, state) {
    return getIsParticipantMediaMuted(participant, MEDIA_TYPE.AUDIO)(state);
}

/**
 * Checks if the participant is video muted.
 *
 * @param {Object} participant - Participant reference.
 * @param {Object} state - Global state.
 * @returns {boolean} - Is video muted for the participant.
 */
export function isParticipantVideoMuted(participant, state) {
    return getIsParticipantMediaMuted(participant, MEDIA_TYPE.VIDEO)(state);
}

/**
 * Returns screenshare track of given virtualScreenshareParticipantId.
 *
 * @param {ITrack[]} tracks - List of all tracks.
 * @param {string} virtualScreenshareParticipantId - Virtual Screenshare Participant ID.
 * @returns {(Track|undefined)}
 */
export function getVirtualScreenshareParticipantTrack(tracks, virtualScreenshareParticipantId) {
    const ownderId = getVirtualScreenshareParticipantOwnerId(virtualScreenshareParticipantId);

    return getScreenShareTrack(tracks, ownderId);
}

/**
 * Returns screenshare track of given owner ID.
 *
 * @param {Track[]} tracks - List of all tracks.
 * @param {string} ownerId - Screenshare track owner ID.
 * @returns {(Track|undefined)}
 */
export function getScreenShareTrack(tracks, ownerId) {
    const matchingTracks = tracks.filter(
        t => Boolean(t.mhTrack)
            && (t.participantId === ownerId || t.mhTrack?.getSourceName?.() === ownerId)
            && (t.mediaType === MEDIA_TYPE.SCREENSHARE || t.videoType === VIDEO_TYPE.DESKTOP)
    );

    if (!matchingTracks.length) {
        return undefined;
    }

    const activeTrack = [ ...matchingTracks ].reverse().find(t => !t.muted);

    return activeTrack ?? matchingTracks[matchingTracks.length - 1];
}

/**
   * Higher-order function that returns a selector for a specific participant.
   *
   * @param {Object} participant - Participant reference.
   * @returns {Function} Selector.
   */
export const getIsParticipantAudioMuted = participant =>

/**
       * Bound selector.
       *
       * @param {Object} state - Global state.
       * @returns {boolean} Is audio muted for the participant.
       */
    state => getIsParticipantMediaMuted(participant, MEDIA_TYPE.AUDIO)(state);

/**
   * Higher-order function that returns a selector for a specific participant.
   *
   * @param {Object} participant - Participant reference.
   * @returns {Function} Selector.
   */
export const getIsParticipantVideoMuted = participant =>

/**
       * Bound selector.
       *
       * @param {Object} state - Global state.
       * @returns {boolean} Is video muted for the participant.
       */
    state => getIsParticipantMediaMuted(participant, MEDIA_TYPE.VIDEO)(state);


/**
 * Creates a local video track for presenter. The constraints are computed based
 * on the height of the desktop that is being shared.
 *
 * @param {Object} options - The options with which the local presenter track
 * is to be created.
 * @param {string|null} [options.cameraDeviceId] - Camera device id or
 * {@code undefined} to use app's settings.
 * @param {number} desktopHeight - The height of the desktop that is being
 * shared.
 * @returns {Promise<MHLocalTrack>}
 */
export async function createLocalPresenterTrack(options, desktopHeight) {
    const { cameraDeviceId } = options;

    // compute the constraints of the camera track based on the resolution
    // of the desktop screen that is being shared.
    const cameraHeights = [ 180, 270, 360, 540, 720 ];
    const proportion = 5;
    const result = cameraHeights.find(
            height => (desktopHeight / proportion) < height);
    const constraints = {
        video: {
            aspectRatio: 4 / 3,
            height: {
                ideal: result
            }
        }
    };
    const [ videoTrack ] = await MeetHourJS.createLocalTracks(
        {
            cameraDeviceId,
            constraints,
            devices: [ 'video' ]
        });

    videoTrack.type = MEDIA_TYPE.PRESENTER;

    return videoTrack;
}

/**
 * Create local tracks of specific types.
 *
 * @param {Object} options - The options with which the local tracks are to be
 * created.
 * @param {string|null} [options.cameraDeviceId] - Camera device id or
 * {@code undefined} to use app's settings.
 * @param {string[]} options.devices - Required track types such as 'audio'
 * and/or 'video'.
 * @param {string|null} [options.micDeviceId] - Microphone device id or
 * {@code undefined} to use app's settings.
 * @param {number|undefined} [options.timeout] - A timeout for MeetHourJS.createLocalTracks used to create the tracks.
 * @param {boolean} [options.firePermissionPromptIsShownEvent] - Whether lib-meet-hour
 * should check for a {@code getUserMedia} permission prompt and fire a
 * corresponding event.
 * @param {boolean} [options.fireSlowPromiseEvent] - Whether lib-meet-hour
 * should check for a slow {@code getUserMedia} request and fire a
 * corresponding event.
 * @param {Object} store - The redux store in the context of which the function
 * is to execute and from which state such as {@code config} is to be retrieved.
 * @returns {Promise<MHLocalTrack[]>}
 */
export function createLocalTracksF(options = {}, store) {
    let { cameraDeviceId, micDeviceId } = options;
    const {
        desktopSharingSourceDevice,
        desktopSharingSources,
        firePermissionPromptIsShownEvent,
        fireSlowPromiseEvent,
        timeout
    } = options;

    let resolvedStore = store;

    if (typeof APP !== 'undefined') {
        // TODO The app's settings should go in the redux store and then the
        // reliance on the global variable APP will go away.
        resolvedStore || (resolvedStore = APP.store);

        const state = resolvedStore.getState();

        if (typeof cameraDeviceId === 'undefined' || cameraDeviceId === null) {
            cameraDeviceId = getUserSelectedCameraDeviceId(state);
        }
        if (typeof micDeviceId === 'undefined' || micDeviceId === null) {
            micDeviceId = getUserSelectedMicDeviceId(state);
        }
    }

    const state = resolvedStore.getState();
    const {
        desktopSharingFrameRate,
        firefox_fake_device,
        resolution
    } = state['features/base/config'];
    const constraints = options.constraints ?? state['features/base/config'].constraints;

    return (
        loadEffects(resolvedStore).then(effectsArray => {
            // Filter any undefined values returned by Promise.resolve().
            const effects = effectsArray.filter(effect => Boolean(effect));

            const createTrackOptions = {
                cameraDeviceId,
                constraints,
                desktopSharingFrameRate,
                desktopSharingSourceDevice,
                desktopSharingSources,

                // Copy array to avoid mutations inside library.
                devices: options.devices.slice(0),
                effects,
                firefox_fake_device,
                firePermissionPromptIsShownEvent,
                fireSlowPromiseEvent,
                micDeviceId,
                resolution,
                timeout
            };

            return MeetHourJS.createLocalTracks(createTrackOptions)
                .catch(err => {
                    // Stale preferred device IDs (common on Windows after USB/Bluetooth re-enumeration)
                    // can fail unmute/create flows. Retry once with default devices.
                    if (_shouldRetryWithoutPreferredDevices(err, createTrackOptions)) {
                        _resetPreferredDeviceSelectionsAfterFailure(resolvedStore, createTrackOptions);
                        logger.warn('Retrying local track creation with default devices after preferred device failure', err);

                        return MeetHourJS.createLocalTracks({
                            ...createTrackOptions,
                            cameraDeviceId: undefined,
                            micDeviceId: undefined
                        });
                    }

                    throw err;
                })

                // Reporting lives here alone. It used to be duplicated in the retry handler above, which then
                // rethrew, so every failure that was not retryable was logged twice.
                .catch(err => {
                    if (_notifyExpectedTrackIssue(err) || _isTeardownError(err)) {
                        logger.warn('Failed to create local tracks', err);
                    } else {
                        logger.error('Failed to create local tracks', err);
                    }

                    return Promise.reject(err);
                });
        }));
}

/**
 * Clears stale persisted camera/mic preference IDs when they cause track creation failures.
 *
 * @param {Object} store - Redux store.
 * @param {Object} createTrackOptions - Options used for createLocalTracks.
 * @returns {void}
 */
function _resetPreferredDeviceSelectionsAfterFailure(store, createTrackOptions) {
    const dispatch = store?.dispatch;

    if (typeof dispatch !== 'function') {
        return;
    }

    const devices = createTrackOptions?.devices || [];
    const hasCameraPreference = devices.includes('video') && Boolean(createTrackOptions?.cameraDeviceId);
    const hasMicPreference = devices.includes('audio') && Boolean(createTrackOptions?.micDeviceId);

    if (!hasCameraPreference && !hasMicPreference) {
        return;
    }

    const settingsUpdate = {};

    if (hasCameraPreference) {
        settingsUpdate.userSelectedCameraDeviceId = undefined;
        settingsUpdate.userSelectedCameraDeviceLabel = undefined;
    }

    if (hasMicPreference) {
        settingsUpdate.userSelectedMicDeviceId = undefined;
        settingsUpdate.userSelectedMicDeviceLabel = undefined;
    }

    dispatch(updateSettings(settingsUpdate));
}

/**
 * Returns true when we should retry local track creation without preferred device ids.
 *
 * @param {Error} error - The createLocalTracks error.
 * @param {Object} createTrackOptions - The options passed to createLocalTracks.
 * @returns {boolean}
 */
function _shouldRetryWithoutPreferredDevices(error, createTrackOptions) {
    const devices = createTrackOptions?.devices || [];
    const hasCameraRequest = devices.includes('video');
    const hasMicRequest = devices.includes('audio');
    const hasPreferredCamera = hasCameraRequest && Boolean(createTrackOptions?.cameraDeviceId);
    const hasPreferredMic = hasMicRequest && Boolean(createTrackOptions?.micDeviceId);

    if (!hasPreferredCamera && !hasPreferredMic) {
        return false;
    }

    const expectedErrorNames = new Set([
        MHTrackErrors.NOT_FOUND,
        MHTrackErrors.CONSTRAINT_FAILED,
        MHTrackErrors.GENERAL,
        MHTrackErrors.TIMEOUT
    ]);

    if (expectedErrorNames.has(error?.name)) {
        return true;
    }

    const details = `${error?.message || ''} ${error?.gum?.message || ''}`.toLowerCase();

    return /device|not found|notreadable|could not start|overconstrained|constraint|in use|busy/.test(details);
}

/**
 * Returns the track if any which corresponds to a specific instance
 * of MHLocalTrack or MHRemoteTrack.
 *
 * @param {ITrack[]} tracks - List of all tracks.
 * @param {(MHLocalTrack|MHRemoteTrack)} mhTrack - mhTrack instance.
 * @returns {(Track|undefined)}
 */
export function getTrackBymhTrack(tracks, mhTrack) {
    return tracks.find(t => t.mhTrack === mhTrack);
}

/**
 * Creates local tracks for preview purposes using the provided options and store.
 *
 * @param {Object} [options={}] - The options for creating local tracks.
 * @param {string} [options.cameraDeviceId] - The ID of the camera device.
 * @param {string} [options.micDeviceId] - The ID of the microphone device.
 * @param {string} [options.desktopSharingSourceDevice] - The source device for desktop sharing.
 * @param {Array} [options.desktopSharingSources] - The sources for desktop sharing.
 * @param {function} [options.firePermissionPromptIsShownEvent] - Callback for permission prompt events.
 * @param {function} [options.fireSlowPromiseEvent] - Callback for slow promise events.
 * @param {number} [options.timeout] - Timeout for creating tracks.
 * @param {Object} [store] - The Redux store.
 * @returns {Promise<[]>} - A promise that resolves with the created local tracks.
 */
export function createLocalTracksForPreview(options = {}, store) {
    let { cameraDeviceId, micDeviceId } = options;
    const {
        desktopSharingSourceDevice,
        desktopSharingSources,
        firePermissionPromptIsShownEvent,
        fireSlowPromiseEvent,
        timeout
    } = options;

    let resolvedStore = store;

    if (typeof APP !== 'undefined') {
        // TODO The app's settings should go in the redux store and then the
        // reliance on the global variable APP will go away.
        resolvedStore || (resolvedStore = APP.store);

        const state = resolvedStore.getState();

        if (typeof cameraDeviceId === 'undefined' || cameraDeviceId === null) {
            cameraDeviceId = getUserSelectedCameraDeviceId(state);
        }
        if (typeof micDeviceId === 'undefined' || micDeviceId === null) {
            micDeviceId = getUserSelectedMicDeviceId(state);
        }
    }

    const state = resolvedStore.getState();
    const {
        desktopSharingFrameRate,
        firefox_fake_device,
        resolution
    } = state['features/base/config'];
    const constraints = options.constraints ?? state['features/base/config'].constraints;

    return (
        loadEffectsForPreview(resolvedStore).then(effectsArray => {
            // Filter any undefined values returned by Promise.resolve().
            const effects = effectsArray.filter(effect => Boolean(effect));

            return MeetHourJS.createLocalTracks(
                {
                    cameraDeviceId,
                    constraints,
                    desktopSharingFrameRate,
                    desktopSharingSourceDevice,
                    desktopSharingSources,

                    // Copy array to avoid mutations inside library.
                    devices: options.devices.slice(0),
                    effects,
                    firefox_fake_device,
                    firePermissionPromptIsShownEvent,
                    fireSlowPromiseEvent,
                    micDeviceId,
                    resolution,
                    timeout
                })
            .catch(err => {
                if (_notifyExpectedTrackIssue(err)) {
                    logger.warn('Failed to create local tracks', err);
                } else {
                    logger.error('Failed to create local tracks', err);
                }

                return Promise.reject(err);
            });
        }));
}

/**
 * Returns an object containing a promise which resolves with the created tracks &
 * the errors resulting from that process.
 *
 * @returns {Promise<MHLocalTrack>}
 *
 * @todo Refactor to not use APP.
 */
export function createPrejoinTracks() {
    const errors = {};
    const initialDevices = [ 'audio' ];
    const requestedAudio = true;
    let requestedVideo = false;
    const { startAudioOnly, startWithAudioMuted, startWithVideoMuted } = APP.store.getState()['features/base/settings'];

    // Always get a handle on the audio input device so that we have statistics even if the user joins the
    // conference muted. Previous implementation would only acquire the handle when the user first unmuted,
    // which would results in statistics ( such as "No audio input" or "Are you trying to speak?") being available
    // only after that point.
    if (startWithAudioMuted) {
        APP.store.dispatch(setAudioMuted(true));
    }

    if (!startWithVideoMuted && !startAudioOnly) {
        initialDevices.push('video');
        requestedVideo = true;
    }

    let tryCreateLocalTracks;

    if (!requestedAudio && !requestedVideo) {
        // Resolve with no tracks
        tryCreateLocalTracks = Promise.resolve([]);
    } else {
        tryCreateLocalTracks = createLocalTracksF({
            devices: initialDevices,
            firePermissionPromptIsShownEvent: true
        })
                .catch(err => {
                    if (requestedAudio && requestedVideo) {

                        // Try audio only...
                        errors.audioAndVideoError = err;

                        return (
                            createLocalTracksF({
                                devices: [ 'audio' ],
                                firePermissionPromptIsShownEvent: true
                            }));
                    } else if (requestedAudio && !requestedVideo) {
                        errors.audioOnlyError = err;

                        return [];
                    } else if (requestedVideo && !requestedAudio) {
                        errors.videoOnlyError = err;

                        return [];
                    }
                    logger.error('Should never happen');
                })
                .catch(err => {
                    // Log this just in case...
                    if (!requestedAudio) {
                        logger.error('The impossible just happened', err);
                    }
                    errors.audioOnlyError = err;

                    // Try video only...
                    return requestedVideo
                        ? createLocalTracksF({
                            devices: [ 'video' ],
                            firePermissionPromptIsShownEvent: true
                        })
                        : [];
                })
                .catch(err => {
                    // Log this just in case...
                    if (!requestedVideo) {
                        logger.error('The impossible just happened', err);
                    }
                    errors.videoOnlyError = err;

                    return [];
                });
    }

    return {
        tryCreateLocalTracks,
        errors
    };
}

/**
 * Returns local audio track.
 *
 * @param {Track[]} tracks - List of all tracks.
 * @returns {(Track|undefined)}
 */
export function getLocalAudioTrack(tracks) {
    return getLocalTrack(tracks, MEDIA_TYPE.AUDIO);
}

/**
 * Returns local track by media type.
 *
 * @param {Track[]} tracks - List of all tracks.
 * @param {MEDIA_TYPE} mediaType - Media type.
 * @param {boolean} [includePending] - Indicates whether a local track is to be
 * returned if it is still pending. A local track is pending if
 * {@code getUserMedia} is still executing to create it and, consequently, its
 * {@code mhTrack} property is {@code undefined}. By default a pending local
 * track is not returned.
 * @returns {(Track|undefined)}
 */
export function getLocalTrack(tracks, mediaType, includePending = false) {
    if (mediaType === MEDIA_TYPE.SCREENSHARE) {
        return getLocalDesktopTrack(tracks, includePending);
    }

    return (
        getLocalTracks(tracks, includePending)
            .find(t => t.mediaType === mediaType));
}

/**
 * Returns an array containing the local tracks with or without a (valid)
 * {@code MHTrack}.
 *
 * @param {Track[]} tracks - An array containing all local tracks.
 * @param {boolean} [includePending] - Indicates whether a local track is to be
 * returned if it is still pending. A local track is pending if
 * {@code getUserMedia} is still executing to create it and, consequently, its
 * {@code mhTrack} property is {@code undefined}. By default a pending local
 * track is not returned.
 * @returns {Track[]}
 */
export function getLocalTracks(tracks, includePending = false) {
    // XXX A local track is considered ready only once it has its `mhTrack`
    // property set by the `TRACK_ADDED` action. Until then there is a stub
    // added just before the `getUserMedia` call with a cancellable
    // `gumInProgress` property which then can be used to destroy the track that
    // has not yet been added to the redux store. Once GUM is cancelled, it will
    // never make it to the store nor there will be any
    // `TRACK_ADDED`/`TRACK_REMOVED` actions dispatched for it.
    return tracks.filter(t => t.local && (t.mhTrack || includePending));
}

/**
 * Returns local video track.
 *
 * @param {Track[]} tracks - List of all tracks.
 * @returns {(Track|undefined)}
 */
export function getLocalVideoTrack(tracks) {
    return getLocalTrack(tracks, MEDIA_TYPE.VIDEO);
}

/**
 * Returns the media type of the local video, presenter or video.
 *
 * @param {Track[]} tracks - List of all tracks.
 * @returns {MEDIA_TYPE}
 */
export function getLocalVideoType(tracks) {
    const presenterTrack = getLocalTrack(tracks, MEDIA_TYPE.PRESENTER);

    return presenterTrack ? MEDIA_TYPE.PRESENTER : MEDIA_TYPE.VIDEO;
}

/**
 * Returns the stored local video track.
 *
 * @param {Object} state - The redux state.
 * @returns {Object}
 */
export function getLocalMHVideoTrack(state) {
    const track = getLocalVideoTrack(state['features/base/tracks']);

    return track?.mhTrack;
}

/**
 * Returns the stored local audio track.
 *
 * @param {Object} state - The redux state.
 * @returns {Object}
 */
export function getLocalMHAudioTrack(state) {
    const track = getLocalAudioTrack(state['features/base/tracks']);

    return track?.mhTrack;
}

/**
 * Returns track of specified media type for specified participant id.
 *
 * @param {Track[]} tracks - List of all tracks.
 * @param {MEDIA_TYPE} mediaType - Media type.
 * @param {string} participantId - Participant ID.
 * @returns {(Track|undefined)}
 */
export function getTrackByMediaTypeAndParticipant(tracks, mediaType, participantId) {
    const matchingTracks = tracks.filter(t =>
        Boolean(t.mhTrack)
        && t.mediaType === mediaType
        && (
            t.videoType === VIDEO_TYPE.DESKTOP
                ? t.mhTrack.getSourceName() === participantId || t.participantId === participantId
                : t.participantId === participantId
        )
    );

    if (!matchingTracks.length) {
        return undefined;
    }

    const activeTrack = [ ...matchingTracks ].reverse().find(t => !t.muted);

    return activeTrack ?? matchingTracks[matchingTracks.length - 1];
}


/**
 * Returns the track if any which corresponds to a specific instance
 * of MHLocalTrack or MHRemoteTrack.
 *
 * @param {Track[]} tracks - List of all tracks.
 * @param {(MHLocalTrack|MHRemoteTrack)} mhTrack - MHTrack instance.
 * @returns {(Track|undefined)}
 */
export function getTrackByMHTrack(tracks, mhTrack) {
    return tracks.find(t => t.mhTrack === mhTrack);
}

/**
 * Returns tracks of specified media type.
 *
 * @param {Track[]} tracks - List of all tracks.
 * @param {MEDIA_TYPE} mediaType - Media type.
 * @returns {Track[]}
 */
export function getTracksByMediaType(tracks, mediaType) {
    return tracks.filter(t => t.mediaType === mediaType);
}

/**
 * Checks if the local video camera track in the given set of tracks is muted.
 *
 * @param {Track[]} tracks - List of all tracks.
 * @returns {Track[]}
 */
export function isLocalCameraTrackMuted(tracks) {
    const presenterTrack = getLocalTrack(tracks, MEDIA_TYPE.PRESENTER);
    const videoTrack = getLocalTrack(tracks, MEDIA_TYPE.VIDEO);

    // Make sure we check the mute status of only camera tracks, i.e.,
    // presenter track when it exists, camera track when the presenter
    // track doesn't exist.
    if (presenterTrack) {
        return isLocalTrackMuted(tracks, MEDIA_TYPE.PRESENTER);
    } else if (videoTrack) {
        return videoTrack.videoType === 'camera'
            ? isLocalTrackMuted(tracks, MEDIA_TYPE.VIDEO) : true;
    }

    return true;
}

/**
 * Checks if the first local track in the given tracks set is muted.
 *
 * @param {Track[]} tracks - List of all tracks.
 * @param {MEDIA_TYPE} mediaType - The media type of tracks to be checked.
 * @returns {boolean} True if local track is muted or false if the track is
 * unmuted or if there are no local tracks of the given media type in the given
 * set of tracks.
 */
export function isLocalTrackMuted(tracks, mediaType) {
    const track = getLocalTrack(tracks, mediaType);

    return !track || track.muted;
}

// /**
//  * Checks if the local video track is of type DESKtOP.
//  *
//  * @param {Object} state - The redux state.
//  * @returns {boolean}
//  */
// export function isLocalVideoTrackDesktop(state) {
//     const videoTrack = getLocalVideoTrack(state['features/base/tracks']);

//     return videoTrack && videoTrack.videoType === VIDEO_TYPE.DESKTOP && !videoTrack.muted;
// }

//     return videoTrack && videoTrack.videoType === VIDEO_TYPE.DESKTOP && !videoTrack.muted;
// }

/**
 * Checks if the local video track is of type DESKtOP.
 *
 * @param {Object} state - The redux state.
 * @returns {boolean}
 */
export function isParticipantScreenSharing(state) {
    const videoTracks = state['features/base/tracks'];

    const isParticipantscreenSharing = videoTracks.filter(participant => participant.videoType === VIDEO_TYPE.DESKTOP);

    return isParticipantscreenSharing.length > 0;
}


/**
 * Get local video track is of type DESKtOP.
 *
 * @param {Object} state - The redux state.
 * @returns {boolean}
 */
export function getLocalVideoTrackDesktop(state) {
    const videoTrack = getLocalVideoTrack(state['features/base/tracks']);

    if (videoTrack.videoType === VIDEO_TYPE.DESKTOP) {
        return videoTrack;
    }

    return null;

}

/**
 * Returns true if the remote track of the given media type and the given
 * participant is muted, false otherwise.
 *
 * @param {Track[]} tracks - List of all tracks.
 * @param {MEDIA_TYPE} mediaType - The media type of tracks to be checked.
 * @param {*} participantId - Participant ID.
 * @returns {boolean}
 */
export function isRemoteTrackMuted(tracks, mediaType, participantId) {
    const track = getTrackByMediaTypeAndParticipant(
        tracks, mediaType, participantId);

    return !track || track.muted;
}

/**
 * Returns whether or not the current environment needs a user interaction with
 * the page before any unmute can occur.
 *
 * @param {Object} state - The redux state.
 * @returns {boolean}
 */
export function isUserInteractionRequiredForUnmute(state) {
    return browser.isUserInteractionRequiredForUnmute()
        && window
        && window.self !== window.top
        && !state['features/base/user-interaction'].interacted;
}

/**
 * Mutes or unmutes a specific {@code MHLocalTrack}. If the muted state of
 * the specified {@code track} is already in accord with the specified
 * {@code muted} value, then does nothing.
 *
 * @param {MHLocalTrack} track - The {@code MHLocalTrack} to mute or
 * unmute.
 * @param {boolean} muted - If the specified {@code track} is to be muted, then
 * {@code true}; otherwise, {@code false}.
 * @returns {Promise}
 */
export function setTrackMuted(track, muted, state,
        dispatch) {
    const coercedMuted = Boolean(muted);
    const f = coercedMuted ? 'mute' : 'unmute';
    const mediaType = track.getType();

    // Ignore the check for desktop track muted operation. When the screenshare is terminated by clicking on the
    // browser's 'Stop sharing' button, the local stream is stopped before the inactive stream handler is fired.
    // We still need to proceed here and remove the track from the peerconnection.
    if (track.isMuted() === coercedMuted && track.getVideoType() !== VIDEO_TYPE.DESKTOP) {
        // The device is already where the caller wants it - but redux is not necessarily. `isMuted()` reads
        // `enabled` straight off the underlying MediaStreamTrack, so any code that flips that flag directly
        // moves the track without dispatching anything. Returning silently here is what turns that momentary
        // drift into a permanently dead control: every subsequent unmute matches this condition and resolves
        // as a no-op, so the button never updates and the local preview stays blank while the track carries
        // on sending to every remote participant. Reconcile the app state instead of walking away from it.
        const reduxTrack = state && getTrackByMHTrack(state['features/base/tracks'], track);

        if (dispatch && reduxTrack && reduxTrack.muted !== coercedMuted) {
            logger.warn(`Local ${mediaType} track was already ${f}d at the device level while the app still `
                + `had it as ${reduxTrack.muted ? 'muted' : 'unmuted'}; reconciling the app state`);

            dispatch({
                type: TRACK_UPDATED,
                track: {
                    mhTrack: track,
                    muted: coercedMuted
                }
            });
        }

        return Promise.resolve();
    }

    _setGUMPendingState(IGUMPendingState.PENDING_UNMUTE, mediaType, coercedMuted, dispatch);

    return track[f]().then(result => {
        _setGUMPendingState(IGUMPendingState.NONE, mediaType, coercedMuted, dispatch);

        return result;
    })
        .catch(error => {
            _setGUMPendingState(IGUMPendingState.NONE, mediaType, coercedMuted, dispatch);

            // Handle specific gUM errors with retry logic instead of immediate failure
            const isGUMError = error?.message?.includes('allocate')
                || error?.message?.includes('NotReadableError')
                || error?.message?.includes('device')
                || error?.name === 'NotAllowedError'
                || error?.name === 'NotReadableError'
                || error?.name === 'TrackStartError'
                || error?.name === MHTrackErrors.DEVICE_BUSY;

            if (isGUMError && !coercedMuted) {
                // For unmute operations, log as warning for device resource errors
                // as these are often transient (browser resource limits, other apps using camera).
                // DEVICE_BUSY (gum.device_busy) is the lib-meet-hour classification for
                // NotReadableError/TrackStartError after its built-in single-shot retry
                // already failed; the user-visible "mic/camera in use by another app" case.
                logger.warn(`set track ${f} failed - device resource error (may retry): ${error.message}`);
            } else if (error?.name === MHTrackErrors.TRACK_IS_DISPOSED) {
                // Reported as a failure but it is a handled, self-repairing one: the rejection below
                // reaches `_setMuted` in the tracks middleware, which dispatches TRACK_MUTE_UNMUTE_FAILED,
                // and the recovery there removes the dead track and adds a freshly created replacement.
                // Only the outcome deserves error level - and `Automatic <type> unmute recovery failed`
                // already covers that - so logging the handoff at error too put a condition the app
                // recovers from on its own into the production error feed as if nothing had handled it.
                // The rejection itself is unchanged; swallowing it is what left the control dead before.
                logger.warn(`set track ${f} failed on an already disposed track; recovering with a `
                    + 'replacement');
            } else {
                // Propagate every failure, TRACK_IS_DISPOSED included. Resolving on a
                // disposed track reported a no-op as success: the redux mute state had
                // already flipped, so the button toggled while the device never did, and
                // TRACK_MUTE_UNMUTE_FAILED was never dispatched to kick off recovery.
                // That is a mic control that looks alive and does nothing.
                logger.error(`set track ${f} failed`, error);
            }

            return Promise.reject(error);
        });
}


/**
 * Gets the default camera facing mode.
 *
 * @param {Object} state - The redux state.
 * @returns {string} - The camera facing mode.
 */
export function getCameraFacingMode(state) {
    return state['features/base/config'].cameraFacingMode ?? CAMERA_FACING_MODE.USER;
}


/**
 * Returns the local desktop track.
 *
 * @param {Track[]} tracks - List of all tracks.
 * @param {boolean} [includePending] - Indicates whether a local track is to be returned if it is still pending.
 * A local track is pending if {@code getUserMedia} is still executing to create it and, consequently, its
 * {@code jitsiTrack} property is {@code undefined}. By default a pending local track is not returned.
 * @returns {(Track|undefined)}
 */
export function getLocalDesktopTrack(tracks, includePending = false) {
    const desktopTracks = getLocalTracks(tracks, includePending)
        .filter(t => t.mediaType === MEDIA_TYPE.SCREENSHARE || t.videoType === VIDEO_TYPE.DESKTOP);

    if (!desktopTracks.length) {
        return undefined;
    }

    const activeTrack = [ ...desktopTracks ].reverse().find(t => Boolean(t.mhTrack) && !t.muted);

    return activeTrack ?? desktopTracks[desktopTracks.length - 1];
}

/**
 * Returns the source names of the screenshare sources in the conference based on the presence shared by the remote
 * endpoints. This should be only used for creating/removing virtual screenshare participant tiles when ssrc-rewriting
 * is enabled. Once the tile is created, the source-name gets added to the receiver constraints based on which the
 * JVB will add the source to the video sources map and signal it to the local endpoint. Only then, a remote track is
 * created/remapped and the tracks in redux will be updated. Once the track is updated in redux, the client will
 * will continue to use the other track based getter functions for other operations related to screenshare.
 *
 * @param {(Function|Object)} stateful - The (whole) redux state, or redux's {@code getState} function to be used to
 * retrieve the state.
 * @returns {string[]}
 */
export function getRemoteScreensharesBasedOnPresence(stateful) {
    const conference = getCurrentConference(stateful);

    return conference?.getParticipants()?.reduce((screenshares, participant) => {
        const sources = participant.getSources();
        const videoSources = sources.get(MEDIA_TYPE.VIDEO);
        const screenshareSources = Array.from(videoSources ?? new Map())
            .filter(source => source[1].videoType === VIDEO_TYPE.DESKTOP && !source[1].muted)
            .map(source => source[0]);


        const updatedScreenshares = [ ...screenshares, ...screenshareSources ];

        return updatedScreenshares;
    }, []);
}


/**
 * Returns track of specified media type for specified participant.
 *
 * @param {IReduxState} state - The redux state.
 * @param {IParticipant} participant - Participant Object.
 * @returns {(Track|undefined)}
 */
export function getVideoTrackByParticipant(
        state,
        participant) {

    if (!participant) {
        return;
    }

    const tracks = state['features/base/tracks'];

    if (isScreenShareParticipant(participant)) {
        return getVirtualScreenshareParticipantTrack(tracks, participant.id);
    }

    return getTrackByMediaTypeAndParticipant(tracks, MEDIA_TYPE.VIDEO, participant.id);
}

/**
 * Is any screen media currently being shared, audio or video.
 *
 * @param {IReduxState} state - The state of the application.
 * @returns {boolean}
 */
export function isScreenMediaShared(state) {
    return isScreenVideoShared(state);
}

/**
 * Is screen sharing currently active.
 *
 * @param {IReduxState} state - The state of the application.
 * @returns {boolean}
 */
export function isScreenVideoShared(state) {
    const tracks = state['features/base/tracks'];
    const localScreenshare = getLocalDesktopTrack(tracks);

    return localScreenshare?.mhTrack && !localScreenshare.mhTrack.isMuted();
}

/**
 * Checks if the local video track is of type DESKtOP.
 *
 * @param {IReduxState} state - The redux state.
 * @returns {boolean}
 */
export function isLocalVideoTrackDesktop(state) {
    const desktopTrack = getLocalDesktopTrack(getTrackState(state));

    return desktopTrack !== undefined && !desktopTrack.muted;
}

/**
 * Sets the GUM pending state for the passed track operation (mute/unmute) and media type.
 * NOTE: We need this only for web.
 *
 * @param {IGUMPendingState} status - The new GUM pending status.
 * @param {MediaType} mediaType - The media type related to the operation (audio or video).
 * @param {boolean} muted - True if the operation is mute and false for unmute.
 * @param {Function} dispatch - The dispatch method.
 * @returns {void}
 */
export function _setGUMPendingState(
        status,
        mediaType,
        muted,
        dispatch) {
    if (!muted && dispatch && typeof APP !== 'undefined') {
        dispatch(gumPending([ mediaType ], status));
    }
}

